










































import { Component, Prop, Mixins } from "vue-property-decorator";
import { ILanguage } from "@/models/constants";
import {
  ITemplate,
  ITemplateContent,
  ITemplateCategory
} from "@/models/templates";
import RequestPromises from "@/mixins/requestPromises";
import { mapState } from "vuex";
import { IState } from "@/store";
import { DataModules } from "@/store/modules/data";
import { getLocaleCountryCode } from "@/data/enums/i18n";
import { ConfirmModalModes } from "@/data/enums";

@Component({
  computed: {
    ...mapState({
      languages: (state: IState) => state.brand.languages
    })
  }
})
export default class NotificationTemplatesContentsTable extends Mixins(
  RequestPromises
) {
  @Prop({ required: true })
  public template!: ITemplate;

  @Prop({ required: true })
  public categories!: ITemplateCategory[];

  @Prop({ default: true })
  public showSubject!: boolean;

  // Data

  public languages!: ILanguage[];

  // Computed

  public get orderedLangs() {
    return this.$_.orderBy(this.languages, ["language"], ["asc"]);
  }

  // Methods

  public canDelete(language: ILanguage) {
    if (!this.$userCan("delete_template_content")) return false;
    return this.hasLanguage(language);
  }

  public hasLanguage(language: ILanguage) {
    return (
      this.$_.findIndex(this.template.contents, [
        "language_id",
        language.id
      ]) !== -1
    );
  }

  public openNotificationFormModal(languageId: ILanguage["id"]) {
    const content = this.$_.find(this.template.contents, [
      "language_id",
      languageId
    ]);

    this.$store.dispatch("ui/open/windowModal", {
      config: {
        parent: this,
        component: () =>
          import(
            "@/components/app/admin/notifications/templates/translationContentsForm.vue"
          ),
        props: {
          languageId,
          languages: this.languages,
          categories: this.categories,
          contentId: this.$_.get(content, "id", ""),
          template: this.template,
          showSubject: this.showSubject
        },
        events: {
          "contents-updated": payload => {
            this.$emit("contents-updated", payload);
          }
        }
      }
    });
  }

  public getLanguageLabel(language: ILanguage) {
    const hasLanguage = this.hasLanguage(language);

    if (hasLanguage && !this.$userCan("update_template_content")) {
      return this.$t("_action.view");
    }

    if (hasLanguage && this.$userCan("update_template_content")) {
      return this.$t("_action.edit_translation");
    }

    if (!hasLanguage && this.$userCan("create_template_content")) {
      return this.$t("_action.add_translation");
    }

    return "";
  }

  public getLocaleCountryCode(code: string) {
    return getLocaleCountryCode(code);
  }

  public async deleteTranslation(lang: ILanguage) {
    const content = this.$_.find(this.template.contents, [
      "language_id",
      lang.id
    ]) as ITemplateContent;
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          mode: ConfirmModalModes.BUTTON,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.templates.confirm_translation_delete", {
            locale: lang.language
          }),
          title: this.$t("_sentence.templates.delete_locale_translation_qn", {
            locale: lang.language
          })
        },
        events: {
          confirmed: async () => {
            try {
              await this.deleteConfirmed(content);
              this.$emit("contents-updated", { templateContents: content });
              this.$store.dispatch("toast/show", {
                message: this.$t(
                  "_sentence.templates.locale_translation_deleted",
                  {
                    locale: lang.language
                  }
                )
              });
              confirm.close();
            } catch (error) {
              this.$store.dispatch("api/handleError", error);
            }
          }
        }
      }
    });
  }

  public deleteConfirmed(content: ITemplateContent) {
    return this.$store.dispatch(
      `data/${DataModules.TEMPLATES_CONTENTS}/remove`,
      {
        id: content.id,
        templateId: content.template_id
      }
    );
  }
}
